// src/components/layout/mobile/MobileHeader.tsx
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, MoreVertical, LogOut, Settings, Moon, Sun, User as UserIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useMobileNavStore } from '@/stores/mobileNavStore';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui';
import { TimerGlobal } from '../TimerGlobal';

interface MobileHeaderProps {
  onLogout: () => void;
}

export function MobileHeader({ onLogout }: MobileHeaderProps) {
  const navigate = useNavigate();
  const { title, showBack } = useMobileNavStore();
  const { user } = useAuthStore();
  const { preferencias, setTema } = usePreferencesStore();

  const isDark = preferencias.tema === 'dark' ||
    (preferencias.tema === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const toggleDarkMode = () => {
    const next = isDark ? 'light' : 'dark';
    setTema(next);
    document.documentElement.classList.toggle('dark', next === 'dark');
  };

  return (
    <header className="sticky top-0 z-40 flex items-center h-12 px-3 bg-background/95 backdrop-blur border-b border-border">
      {/* Left: Back button or logo */}
      <div className="flex items-center min-w-[40px]">
        {showBack ? (
          <button
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/actividades')}
            className="min-w-touch min-h-touch flex items-center justify-center -ml-1 rounded-md active:bg-muted/50 transition-colors"
            aria-label="Volver"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        ) : (
          <span className="text-sm font-bold text-primary">Q</span>
        )}
      </div>

      {/* Center: Title */}
      <h1 className="flex-1 text-center text-sm font-semibold truncate px-2">
        {title}
      </h1>

      {/* Right: Timer + Menu */}
      <div className="flex items-center gap-1">
        <TimerGlobal />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/configuracion/perfil')}>
              <UserIcon className="h-4 w-4 mr-2" />
              {user?.nombre || 'Perfil'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={toggleDarkMode}>
              {isDark ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
              {isDark ? 'Modo claro' : 'Modo oscuro'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/configuracion')}>
              <Settings className="h-4 w-4 mr-2" />
              Configuración
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-destructive">
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
